import React, { useContext } from "react";

import { useAppContext } from "../hooks/useAppContext";
import { useBusyBlockerContext } from "./busy-blocker-context";

export type IDashboardContext = Omit<eXpress.creator.IDashboard, "textZone">;

/** Create the value for the React DashboardContext */
function createContextValue({
	editor,
	fontFamilyLoader,
	fallbackFontFamily,
	busyBlocker,
}: {
	editor: eXpress.core.IEditor;
	fontFamilyLoader: eXpress.creator.rendering.FontFamilyLoader;
	fallbackFontFamily: eXpress.core.FontFamily;
	busyBlocker: eXpress.IBalancedScope;
}): IDashboardContext {
	const dashboardFontHelper = new eXpress.creator.DashboardFontHelper({
		fallbackFontFamily,
		logger: editor.logger,
		logErrorAsync: editor.client.logErrorAsync.bind(editor.client),
		fontFamilyMetricsLoader: fontFamilyLoader.fontFamilyMetricsLoader,
		fontFamilyContentLoader: fontFamilyLoader.fontFamilyContentLoader,
		getMinFontSizeForProductCodeAsync: async (fontFamilyName, productCode) => {
			const result = await editor.client.getFontFamiliesInfoForProduct(
				productCode,
				editor.assets.fontFamilies,
			);
			return result.minFontSizes[fontFamilyName.toLowerCase()] ?? 0;
		},
	});

	const textZoneDashboard = new eXpress.TextZoneDashboard({
		editor,
		client: editor.client,
		busyHandler: busyBlocker,
		dashboardFontHelper,
	});

	return new eXpress.creator.Dashboard(editor, editor.client, busyBlocker, textZoneDashboard);
}

// Create Context
const DashboardContext = React.createContext<IDashboardContext>({} as IDashboardContext);
DashboardContext.displayName = "DashboardContext";

/**
 * Component that provides a dashboard for mutation the definition
 */
export function DashboardContextProvider(
	props: React.PropsWithChildren<{
		context?: IDashboardContext;
	}>,
) {
	const { children, context: initialContext } = props;

	const { editor, fontFamilyLoader, defaultFontFamily: fallbackFontFamily } = useAppContext();

	const { busyBlocker } = useBusyBlockerContext();

	const contextValue =
		initialContext ||
		createContextValue({
			editor,
			fontFamilyLoader,
			fallbackFontFamily,
			busyBlocker,
		});

	// Make dashboardContext available on console in 'Debug' mode
	// Assuming only one context is used, else global variable overridden
	if (eXpress.isDebugMode) {
		// Allow, this is only used for debugging
		// eslint-disable-next-line react-compiler/react-compiler
		(window as any)._dashboardContext = contextValue;
	}

	// Create React AppContext value
	return <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>;
}

/** Hook to get the dashboard */
export const useDashboardContext = () => {
	const context = useContext(DashboardContext);
	// Because the default uses an empty object, we check a dashboard function
	if (!context.applyPhotoToZone) {
		throw new eXpress.Exception(
			"useDashboardContext requires a parent DashboardContextProvider element",
		);
	}
	return context;
};
